import type { Snippet, SnippetTreeNode } from "@/v2/entities/KnowledgeSnippet";

interface TreeNodeMap {
  [key: string]: SnippetTreeNode;
}

export const pathToTree = (snippets: Snippet[]): SnippetTreeNode[] => {
  const nodeMap: TreeNodeMap = {};
  const roots: SnippetTreeNode[] = [];

  // Helper function to get or create a node
  const getOrCreateNode = (
    path: string,
    name: string,
    isFile: boolean,
    snippet?: Snippet
  ): SnippetTreeNode => {
    // Use different ID schemes for folders vs files to avoid conflicts
    // Folders: append '/' to the path
    // Files: use snippet ID to ensure uniqueness when multiple snippets have the same title and path
    const nodeId = isFile && snippet ? snippet.id : `${path}/`;

    if (nodeMap[nodeId]) {
      return nodeMap[nodeId];
    }

    const node: SnippetTreeNode = {
      id: nodeId,
      name,
      type: isFile ? "file" : "folder",
      children: isFile ? undefined : [],
      snippetId: isFile ? snippet?.id : undefined,
      path: isFile ? path : undefined,
      isVerifiedVersionAvailable: isFile
        ? snippet?.isVerifiedVersionAvailable
        : undefined,
      isInternal: isFile ? snippet?.isInternal : undefined,
    };

    nodeMap[nodeId] = node;
    return node;
  };

  // Process each snippet
  snippets.forEach((snippet) => {
    // Remove leading slash and split path
    const cleanPath = snippet.path.startsWith("/")
      ? snippet.path.slice(1)
      : snippet.path;
    const parts = cleanPath ? cleanPath.split("/") : [];

    // Add the snippet title as the final part (file node)
    const allParts = [...parts, snippet.title];

    let currentPath = "";
    let parentNode: SnippetTreeNode | null = null;

    // Process each part of the path
    allParts.forEach((part, index) => {
      const isLastPart = index === allParts.length - 1;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      const node = getOrCreateNode(
        currentPath,
        part,
        isLastPart,
        isLastPart ? snippet : undefined
      );

      if (parentNode) {
        // Add node to parent's children if not already there
        if (
          parentNode.children &&
          !parentNode.children.some((child) => child.id === node.id)
        ) {
          parentNode.children.push(node);
        }
      } else {
        // This is a root node
        if (!roots.some((root) => root.id === node.id)) {
          roots.push(node);
        }
      }

      parentNode = node;
    });
  });

  // Calculate snippet counts for folders
  const calculateSnippetCount = (node: SnippetTreeNode): number => {
    if (node.type === "file") {
      return 1;
    }

    if (!node.children || node.children.length === 0) {
      return 0;
    }

    const count = node.children.reduce((total, child) => {
      return total + calculateSnippetCount(child);
    }, 0);

    node.snippetCount = count;
    return count;
  };

  roots.forEach((root) => calculateSnippetCount(root));

  return roots;
};
